import React, { useCallback, useMemo, useEffect } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import QuestionCircleIcon from '@atlaskit/icon/core/migration/question-circle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ErrorRetry from '@atlassian/jira-software-devops-report-common-error-retry/src/index.tsx';
import {
	CHART_RIGHT_SPACE_LEFT_PADDING,
	CHART_RIGHT_SPACE_WIDTH,
} from '../../common/constants.tsx';
import type {
	BarChartDataPoint,
	DeploymentFrequencyReportWeekData,
	EChartsEvent,
} from '../../common/types.tsx';
import {
	CHART_HEIGHT as BAR_CHART_HEIGHT,
	CHART_TITLE_HEIGHT,
	CHART_TITLE_MARGIN_BOTTOM,
	SELECTION_DETAIL_BOTTOM_PADDING,
} from '../../common/ui/deployment-frequency-bar-chart/constants.tsx';
import { CHART_HEIGHT as LINE_CHART_HEIGHT } from '../../common/ui/issues-per-deployment-chart/constants.tsx';
import { PerformanceTracker } from '../../common/ui/performance-tracker/index.tsx';
import { useSelectedWeekStore } from '../../controllers/selected-date-range/index.tsx';
import {
	getSelectedWeekData,
	useSmartWeekSelect,
} from '../../controllers/selected-date-range/utils.tsx';
import { useReportCoreData } from '../../services/deployment-frequency-core/index.tsx';
import { getProdDataState } from '../../services/deployment-frequency-core/utils.tsx';
import { useViewDeploymentFrequencyReportExperienceFail } from '../../services/experience-tracker/index.tsx';
import { Chart } from './chart/index.tsx';
import { convertReportCoreToChartDataPoints } from './chart/utils.tsx';
import { EmptyDataMessage } from './empty-data-message/index.tsx';
import { LoadingSkeleton } from './loading-skeleton/index.tsx';
import messages from './messages.tsx';
import { NoProvider } from './no-provider/index.tsx';
import { SelectionDetail } from './selection-detail/index.tsx';

const Title = () => {
	const { formatMessage } = useIntl();
	return (
		<Heading as="h2" size="xsmall">
			<ChartTitle>
				{formatMessage(messages.chartTitle)}
				<Tooltip content={formatMessage(messages.titleHelpTooltip)} position="right">
					<QuestionIcon>
						<QuestionCircleIcon LEGACY_size="small" label="" />
					</QuestionIcon>
				</Tooltip>
			</ChartTitle>
		</Heading>
	);
};

export const DeploymentFrequencyBarChart = () => {
	const onViewDeploymentFrequencyReportExperienceFail =
		useViewDeploymentFrequencyReportExperienceFail();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error, fetch } = useReportCoreData();
	const [{ selectedWeekStart }, { setSelectedWeekStart }] = useSelectedWeekStore();
	const onChartClicked = useCallback(
		({ data: eventData }: EChartsEvent<BarChartDataPoint>) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'deploymentFrequencyBar',
					actionSubjectId: 'deploymentFrequencyReport',
				}),
			);
			setSelectedWeekStart(eventData.customInfo.startDateInclusive);
		},
		[createAnalyticsEvent, setSelectedWeekStart],
	);
	const events = useMemo(() => ({ click: onChartClicked }), [onChartClicked]);
	// @ts-expect-error - TS2345 - Argument of type '(newWeekStartDate: Date) => void | Promise<void>' is not assignable to parameter of type '(newWeekStartDate: Date) => Action<State>'.
	useSmartWeekSelect(data, selectedWeekStart, setSelectedWeekStart);

	useEffect(() => {
		if (error && data == null) {
			onViewDeploymentFrequencyReportExperienceFail('BarChart', error);
			fireErrorAnalytics({
				meta: {
					id: 'BarChartDataFailed',
					packageName: 'jiraSoftwareDeploymentFrequencyReport',
					teamName: 'fusion-solaris',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [data, error, onViewDeploymentFrequencyReportExperienceFail]);

	if (error && data == null) {
		return (
			<>
				<Title />
				<ErrorUIWrapper data-test-id="software-deployment-frequency-report.ui.deployment-frequency-bar-chart.error-ui">
					<ErrorRetry onRetry={fetch} />
				</ErrorUIWrapper>
			</>
		);
	}
	if (loading || data == null || selectedWeekStart === null) {
		return <LoadingSkeleton />;
	}
	if (error) {
		// TODO - We would want to fire analytics?
	}

	const { averageDeploymentCount: averageDeploymentCountWholePeriod, hasDeploymentProvider } = data;

	if (hasDeploymentProvider === false) {
		return <NoProvider />;
	}

	const prodDataState = getProdDataState(data);
	const dataPoints: BarChartDataPoint[] = convertReportCoreToChartDataPoints(
		data,
		selectedWeekStart,
	);
	const selectedWeekData: DeploymentFrequencyReportWeekData = getSelectedWeekData(
		data.weeksData,
		selectedWeekStart,
	);

	return (
		<>
			{/* Performance tracker is located here because this chart is the first "meaningful" data the report renders. */}
			<PerformanceTracker />
			<Title />
			{prodDataState === 'COMPLETELY_EMPTY' ? (
				<EmptyMessageWrapper>
					<EmptyDataMessage />
				</EmptyMessageWrapper>
			) : null}
			<ChartContainer data-test-id="software-deployment-frequency-report.ui.deployment-frequency-bar-chart">
				<ChartItem>
					<Chart
						dataPoints={dataPoints}
						averageDeploymentsWholePeriod={averageDeploymentCountWholePeriod}
						onEvents={events}
						shouldDisplayAverageLine={prodDataState === 'SUFFICIENT_FOR_TREND'}
					/>
				</ChartItem>
				<SelectionDetailItem>
					<SelectionDetail
						weekCount={selectedWeekData.deploymentCount.prod}
						weekStartDate={selectedWeekData.startDateInclusive}
						wholePeriodAverage={averageDeploymentCountWholePeriod}
						prodDataState={prodDataState}
					/>
				</SelectionDetailItem>
			</ChartContainer>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorUIWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${BAR_CHART_HEIGHT + LINE_CHART_HEIGHT}px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartTitle = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${CHART_TITLE_HEIGHT}px`,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `0 0 ${CHART_TITLE_MARGIN_BOTTOM}px 0`,
	paddingLeft: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const QuestionIcon = styled.div({
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
	cursor: 'pointer',
	paddingTop: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyMessageWrapper = styled.div({
	marginTop: token('space.200'),
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: 0,
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${BAR_CHART_HEIGHT}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartItem = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${CHART_RIGHT_SPACE_WIDTH}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectionDetailItem = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${CHART_RIGHT_SPACE_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${CHART_RIGHT_SPACE_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${CHART_RIGHT_SPACE_LEFT_PADDING}px`,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingBottom: `${SELECTION_DETAIL_BOTTOM_PADDING}px`,
});
